import { useState, useRef, useCallback } from 'react';
import Navigation from '../components/Navigation';
import './ImageEnhancerPage.css';

import MetaSEO from '../components/MetaSEO';

const ImageEnhancerPage = () => {
  // State management
  const [images, setImages] = useState([]);
  const [enhancedImages, setEnhancedImages] = useState({});
  const [processing, setProcessing] = useState({});
  const [selectedPreset, setSelectedPreset] = useState('auto');
  const [enhancementStrength, setEnhancementStrength] = useState(75);
  const [upscaleFactor, setUpscaleFactor] = useState(2);
  const [dragActive, setDragActive] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [comparisonSlider, setComparisonSlider] = useState(50);
  
  const fileInputRef = useRef(null);
  const sliderRef = useRef(null);

  // Enhancement presets
  const presets = {
    auto: {
      name: 'Auto Enhance',
      description: 'Let AI automatically fix your image',
      icon: '🤖',
      tip: 'Best for everyday photos',
      config: { denoise: true, upscale: true, faceRestore: true, colorCorrect: true }
    },
    portrait: {
      name: 'Portrait',
      description: 'Perfect for selfies and people photos',
      icon: '👤',
      tip: 'Enhances faces and skin tones',
      config: { denoise: true, upscale: true, faceRestore: true, colorCorrect: true, faceEnhanceStrength: 100 }
    },
    lowlight: {
      name: 'Low Light',
      description: 'Brighten dark or dim photos',
      icon: '🌙',
      tip: 'Great for indoor or night shots',
      config: { denoise: true, upscale: true, brighten: true, colorCorrect: true }
    },
    product: {
      name: 'Product',
      description: 'Make product photos pop',
      icon: '📦',
      tip: 'Perfect for online listings',
      config: { denoise: true, upscale: true, sharpen: true, colorCorrect: false }
    },
    oldphoto: {
      name: 'Old Photo',
      description: 'Restore vintage photos',
      icon: '📷',
      tip: 'Fixes scratches and fading',
      config: { denoise: true, upscale: true, scratchRemoval: true, colorCorrect: true, faceRestore: true }
    },
    scan: {
      name: 'Document',
      description: 'Sharpen text and documents',
      icon: '📄',
      tip: 'Makes scanned docs clearer',
      config: { denoise: true, upscale: true, sharpen: true, contrastEnhance: true }
    }
  };

  // File validation
  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!validTypes.includes(file.type)) {
      return { valid: false, error: 'Please upload JPG, PNG, or WebP images only' };
    }
    if (file.size > maxSize) {
      return { valid: false, error: 'File is too large. Maximum size is 10MB' };
    }
    return { valid: true };
  };

  // Handle file upload
  const handleFileUpload = useCallback((files) => {
    const fileArray = Array.from(files);
    const validFiles = [];

    fileArray.forEach((file, index) => {
      const validation = validateFile(file);
      if (validation.valid) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const id = `img_${Date.now()}_${index}`;
          validFiles.push({
            id,
            file,
            name: file.name,
            size: file.size,
            preview: e.target.result,
            status: 'pending'
          });
          
          if (validFiles.length === fileArray.filter(f => validateFile(f).valid).length) {
            setImages(prev => [...prev, ...validFiles]);
          }
        };
        reader.readAsDataURL(file);
      }
    });
  }, []);

  // Drag and drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [handleFileUpload]);

  // Enhance single image
  const enhanceImage = async (imageData) => {
    const { id, file } = imageData;
    
    setProcessing(prev => ({ ...prev, [id]: { status: 'processing', progress: 0 } }));

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('preset', selectedPreset);
      formData.append('strength', enhancementStrength);
      formData.append('upscale_factor', upscaleFactor);
      formData.append('config', JSON.stringify(presets[selectedPreset].config));

      const response = await fetch('/api/enhance', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Enhancement failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.enhanced_image) {
        setEnhancedImages(prev => ({
          ...prev,
          [id]: {
            url: result.enhanced_image,
            metadata: result.metadata
          }
        }));
        setProcessing(prev => ({ ...prev, [id]: { status: 'complete', progress: 100 } }));
      }
    } catch (error) {
      console.error('Enhancement error:', error);
      setProcessing(prev => ({ ...prev, [id]: { status: 'error', error: error.message } }));
    }
  };

  // Enhance all images
  const enhanceAllImages = () => {
    images.forEach(image => {
      if (!enhancedImages[image.id] && !processing[image.id]) {
        enhanceImage(image);
      }
    });
  };

  // Download image
  const downloadImage = (imageId) => {
    const enhanced = enhancedImages[imageId];
    if (!enhanced) return;

    const link = document.createElement('a');
    link.href = enhanced.url;
    link.download = `enhanced_${images.find(img => img.id === imageId)?.name || 'image.png'}`;
    link.click();
  };

  // Remove image
  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
    setEnhancedImages(prev => {
      const newState = { ...prev };
      delete newState[imageId];
      return newState;
    });
    setProcessing(prev => {
      const newState = { ...prev };
      delete newState[imageId];
      return newState;
    });
    if (selectedImage === imageId) {
      setSelectedImage(null);
    }
  };

  // Clear all
  const clearAll = () => {
    if (window.confirm('Remove all images?')) {
      setImages([]);
      setEnhancedImages({});
      setProcessing({});
      setSelectedImage(null);
    }
  };

  // Comparison slider
  const handleSliderMove = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setComparisonSlider(percentage);
  };

  const pendingCount = images.length - Object.keys(enhancedImages).length;

  return (
    <div className="ie-page">
      <MetaSEO 
        title="AI Image Upscaler & Enhancer Studio"
        description="Free online AI image enhancer: upscale resolution, remove noise, fix low-light photos, restore old pictures, and compare before/after results."
        keywords="AI image enhancer, image upscaler, photo restorer, denoise image, low light photo fixer, free image upscale"
      />
      <Navigation />
      
      {/* Hero Section */}
      <div className="ie-hero">
        <div className="ie-hero-content">
          <h1>✨ AI Image Enhancer</h1>
          <p className="ie-hero-subtitle">Transform your photos with professional AI enhancement</p>
          <p className="ie-hero-description">
            Upscale, restore, and perfect your images in seconds. No signup required!
          </p>
        </div>
      </div>

      <div className="ie-container">
        
        {/* Upload Section */}
        <section className="ie-section">
          <h2>Upload Your Images</h2>
          <div
            className={`upload-area ${dragActive ? 'ie-drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={(e) => handleFileUpload(e.target.files)}
              style={{ display: 'none' }}
            />
            <div className="ie-upload-icon">📁</div>
            <h3>Drop your images here</h3>
            <p>or click to browse</p>
            <div className="ie-upload-info">
              <span>✓ JPG, PNG, WebP</span>
              <span>✓ Up to 10MB each</span>
              <span>✓ Multiple files OK</span>
            </div>
          </div>

          {images.length > 0 && (
            <div className="ie-upload-stats">
              <div className="ie-stat">
                <span className="ie-stat-number">{images.length}</span>
                <span className="ie-stat-label">Images uploaded</span>
              </div>
              <div className="ie-stat">
                <span className="ie-stat-number">{Object.keys(enhancedImages).length}</span>
                <span className="ie-stat-label">Enhanced</span>
              </div>
              <div className="ie-stat">
                <span className="ie-stat-number">{pendingCount}</span>
                <span className="ie-stat-label">Remaining</span>
              </div>
            </div>
          )}
        </section>

        {/* Settings Section */}
        {images.length > 0 && (
          <>
            <section className="ie-section">
              <h2>Choose Enhancement Style</h2>
              <div className="ie-presets-grid">
                {Object.entries(presets).map(([key, preset]) => (
                  <div
                    key={key}
                    className={`preset-card ${selectedPreset === key ? 'active' : ''}`}
                    onClick={() => setSelectedPreset(key)}
                  >
                    <span className="ie-preset-icon">{preset.icon}</span>
                    <h3>{preset.name}</h3>
                    <p className="ie-preset-desc">{preset.description}</p>
                    <p className="ie-preset-tip">{preset.tip}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="ie-section">
              <h2>Adjust Settings</h2>
              <div className="ie-controls-grid">
                <div className="ie-control-group">
                  <label>
                    <span>Enhancement Strength</span>
                    <strong>{enhancementStrength}%</strong>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={enhancementStrength}
                    onChange={(e) => setEnhancementStrength(Number(e.target.value))}
                    className="ie-slider"
                  />
                  <p className="ie-control-help">Higher = stronger effect</p>
                </div>

                <div className="ie-control-group">
                  <label>
                    <span>Image Size</span>
                    <strong>{upscaleFactor}x</strong>
                  </label>
                  <div className="ie-scale-buttons">
                    {[1, 2, 4].map(factor => (
                      <button
                        key={factor}
                        className={`scale-btn ${upscaleFactor === factor ? 'active' : ''}`}
                        onClick={() => setUpscaleFactor(factor)}
                      >
                        {factor}x
                        <span>{factor === 1 ? 'Same size' : factor === 2 ? 'Double' : 'Quadruple'}</span>
                      </button>
                    ))}
                  </div>
                  <p className="ie-control-help">1x = no resize, 2x = 2× larger, 4x = 4× larger</p>
                </div>
              </div>

              <div className="ie-action-bar">
                <button
                  className="ie-btn-primary ie-btn-large"
                  onClick={enhanceAllImages}
                  disabled={pendingCount === 0}
                >
                  ✨ Enhance {pendingCount > 0 ? `${pendingCount} Image${pendingCount > 1 ? 's' : ''}` : 'All'}
                </button>
                <button className="ie-btn-secondary" onClick={clearAll}>
                  Clear All
                </button>
              </div>
            </section>
          </>
        )}

        {/* Images Grid */}
        {images.length > 0 && (
          <section className="ie-section">
            <h2>Your Images</h2>
            <div className="ie-images-grid">
              {images.map(image => {
                const isProcessing = processing[image.id];
                const enhanced = enhancedImages[image.id];

                return (
                  <div key={image.id} className="ie-image-card">
                    <div className="ie-image-preview">
                      <img src={enhanced?.url || image.preview} alt={image.name} />
                      
                      {isProcessing && isProcessing.status === 'processing' && (
                        <div className="ie-processing-overlay">
                          <div className="ie-spinner"></div>
                          <p>Enhancing...</p>
                        </div>
                      )}
                      
                      {enhanced && (
                        <div className="ie-enhanced-badge">✓ Enhanced</div>
                      )}

                      <button
                        className="ie-remove-btn"
                        onClick={() => removeImage(image.id)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>

                    <div className="ie-image-details">
                      <h4>{image.name}</h4>
                      <p>{(image.size / 1024).toFixed(0)} KB</p>
                    </div>

                    <div className="ie-image-actions">
                      {enhanced ? (
                        <>
                          <button
                            className="ie-btn-action ie-btn-compare"
                            onClick={() => setSelectedImage(image.id)}
                          >
                            👁️ Compare
                          </button>
                          <button
                            className="ie-btn-action ie-btn-download"
                            onClick={() => downloadImage(image.id)}
                          >
                            ⬇️ Download
                          </button>
                        </>
                      ) : (
                        <button
                          className="ie-btn-action ie-btn-enhance"
                          onClick={() => enhanceImage(image)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? 'Processing...' : '✨ Enhance'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Empty State */}
        {images.length === 0 && (
          <section className="ie-section ie-empty-state">
            <div className="ie-empty-content">
              <div className="ie-empty-icon">🖼️</div>
              <h2>No images yet</h2>
              <p>Upload your first image to get started!</p>
              <button
                className="ie-btn-primary ie-btn-large"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose Images
              </button>
            </div>
          </section>
        )}
      </div>

      {/* Comparison Modal */}
      {selectedImage && enhancedImages[selectedImage] && (
        <div className="ie-comparison-overlay" onClick={() => setSelectedImage(null)}>
          <div className="ie-comparison-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ie-modal-close" onClick={() => setSelectedImage(null)}>×</button>
            
            <h2>Before & After</h2>
            <p className="ie-comparison-instruction">Drag the slider to compare</p>
            
            <div
              ref={sliderRef}
              className="ie-comparison-container"
              onMouseMove={handleSliderMove}
              onTouchMove={handleSliderMove}
            >
              <img
                src={images.find(img => img.id === selectedImage)?.preview}
                alt="Original"
                className="ie-comparison-image original"
              />
              <div
                className="ie-comparison-overlay-image"
                style={{ clipPath: `inset(0 ${100 - comparisonSlider}% 0 0)` }}
              >
                <img
                  src={enhancedImages[selectedImage].url}
                  alt="Enhanced"
                  className="ie-comparison-image enhanced"
                />
              </div>
              <div className="ie-slider-handle" style={{ left: `${comparisonSlider}%` }}>
                <div className="ie-slider-line"></div>
                <div className="ie-slider-button">⇄</div>
              </div>
            </div>

            <div className="ie-comparison-labels">
              <span className="ie-label-before">Before</span>
              <span className="ie-label-after">After</span>
            </div>

            <button
              className="ie-btn-primary"
              onClick={() => downloadImage(selectedImage)}
            >
              ⬇️ Download Enhanced Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageEnhancerPage;
